import { Typography, Box, Link, Grid2 } from '@mui/material';
import { useState, useEffect } from 'react';
import Lightbox, { Slide } from 'yet-another-react-lightbox';
import Download from 'yet-another-react-lightbox/plugins/download';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';

import { CollapsibleSection } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list-collapsible-section';
import { JsmIssueBase } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';
import { Author } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';
import { MojiraBugtrackerBackendPaths } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-resolver';

interface AttachmentsCollapseProps {
  issue: JsmIssueBase;
  expanded: boolean;
  toggleSection: () => void;
}

interface Attachment {
  content: string;
  thumbnail: string | undefined;
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  created: string;
  author: Author;
}

export function AttachmentsCollapse({
  issue,
  expanded,
  toggleSection,
}: AttachmentsCollapseProps) {
  const [open, setOpen] = useState(false);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [safeAttachments, setSafeAttachments] = useState<Attachment[]>([]);

  const getProxyUrl = (sourceUrl: string | undefined): string => {
    if (!sourceUrl) return '';

    const attachmentId = sourceUrl.match(/attachment\/content\/(\d+)/)?.[1];
    if(!attachmentId) return '';

    return `${window.location.origin}/api/${MojiraBugtrackerBackendPaths.issueAttachmentGet}?attachmentId=${attachmentId}`;
  };

  useEffect(() => {
    if (!issue?.fields?.attachment || issue.fields.attachment.length === 0) {
      setLoading(false);
      return;
    }

    const processedAttachments = issue.fields.attachment.map((attachment) => ({
      ...attachment,
      content: getProxyUrl(attachment.content || attachment.thumbnail),
      thumbnail: attachment.thumbnail
        ? getProxyUrl(attachment.thumbnail)
        : undefined,
    }));

    setSafeAttachments(processedAttachments);

    const newSlides = processedAttachments.map((attachment) =>
      attachment.mimeType === 'video/mp4'
        ? {
            type: 'video' as const,
            width: 1280,
            height: 720,
            poster: attachment.thumbnail || attachment.content,
            sources: [
              {
                src: attachment.content,
                type: 'video/mp4',
              },
            ],
            download: attachment.content,
          }
        : {
            src: attachment.content,
            download: attachment.content,
          }
    );
    setSlides(newSlides);
    setLoading(false);
  }, [issue?.fields?.attachment]);

  const handleImageClick = (index: number) => {
    setCurrentSlideIndex(index);
    setOpen(true);
  };

  return loading ? (
    <div></div>
  ) : (
    <CollapsibleSection
      title="Attachments"
      expanded={expanded}
      setExpanded={toggleSection}
    >
      <Grid2 container spacing={3} columns={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        {safeAttachments.map((attachment, index) => (
          <Grid2
            key={index}
            sx={{
              flexGrow: 1,
              maxWidth: '250px',
              flexBasis: '250px',
            }}
          >
            <Box
              sx={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: 2,
                height: '250px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              {attachment.mimeType.startsWith('video/') ? (
                <Box
                  sx={{
                    height: '150px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: 1,
                  }}
                >
                  <video
                    poster={attachment.thumbnail || attachment.content}
                    controls
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <source
                      src={attachment.content}
                      type={attachment.mimeType}
                    />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '150px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: 1,
                  }}
                >
                  <img
                    src={attachment.content}
                    alt={attachment.filename}
                    loading="lazy"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                    onClick={() => handleImageClick(index)}
                  />
                </Box>
              )}

              <Box
                sx={{
                  width: '100%',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textAlign: 'center',
                }}
              >
                <Link
                  component="button"
                  onClick={() => handleImageClick(index)}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  {attachment.filename}
                </Link>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Size: {(attachment.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </Grid2>
        ))}
      </Grid2>

      <Lightbox
        open={open}
        plugins={[Video, Download]}
        close={() => setOpen(false)}
        slides={slides}
        index={currentSlideIndex}
      />
    </CollapsibleSection>
  );
}
